/**
 * Full scope editor for the personal dashboard (includes workflows).
 * Includes ALL dimensions including Workflows
 */

import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  MOCK_PRODUCTS,
  MOCK_PRODUCT_GROUPS,
  MOCK_NODES,
  MOCK_FOUNDRIES,
  MOCK_BUSINESS_UNITS,
  MOCK_TEST_STAGES,
} from '../scope-mock-options'
import { WORKFLOW_MANIFESTS } from '../mock-multi-workflow-data'
import type { DashboardScope, DateRange } from '../types'

interface ScopeEditorFullProps {
  currentScope: DashboardScope
  onSave: (newScope: DashboardScope) => void
  onCancel: () => void
}

export function ScopeEditorFull({ currentScope, onSave, onCancel }: ScopeEditorFullProps) {
  const [selectedProducts, setSelectedProducts] = useState<string[]>(currentScope.products || [])
  const [selectedNodes, setSelectedNodes] = useState<string[]>(currentScope.nodes || [])
  const [selectedBUs, setSelectedBUs] = useState<string[]>(currentScope.businessUnits || [])
  const [selectedFoundries, setSelectedFoundries] = useState<string[]>(currentScope.foundries || [])
  const [selectedWorkflows, setSelectedWorkflows] = useState<string[]>(currentScope.workflows || [])
  const [dateRange, setDateRange] = useState<DateRange>(currentScope.dateRange)

  const toggleItem = (item: string, list: string[], setter: (list: string[]) => void) => {
    if (list.includes(item)) {
      setter(list.filter((i) => i !== item))
    } else {
      setter([...list, item])
    }
  }

  const handleSave = () => {
    const newScope: DashboardScope = {
      products: selectedProducts.length > 0 ? selectedProducts : undefined,
      nodes: selectedNodes.length > 0 ? selectedNodes : undefined,
      businessUnits: selectedBUs.length > 0 ? selectedBUs : undefined,
      foundries: selectedFoundries.length > 0 ? selectedFoundries : undefined,
      workflows: selectedWorkflows.length > 0 ? selectedWorkflows : undefined,
      dateRange,
    }
    onSave(newScope)
  }

  const hasSelections =
    selectedProducts.length > 0 ||
    selectedNodes.length > 0 ||
    selectedBUs.length > 0 ||
    selectedFoundries.length > 0 ||
    selectedWorkflows.length > 0

  return (
    <div className="space-y-6 p-4">
      <div className="rounded-xl border border-[#e6e6e6] bg-[#fafafa] p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium tracking-[0.14px] text-[#1d1d1d]">Dashboard Scope Configuration</p>
            <p className="mt-1 text-xs tracking-[0.12px] text-[#727272]">
              {hasSelections
                ? 'All widgets will show data matching these filters'
                : 'No filters selected = Portfolio view (all products, all workflows, all data)'}
            </p>
          </div>
        </div>
      </div>

      {/* Date Range */}
      <div className="space-y-2">
        <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Date Range</Label>
        <Select
          value={dateRange}
          onValueChange={(value) => setDateRange(value as DateRange)}
        >
          <SelectTrigger className="h-10 rounded-xl border-[#e6e6e6]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="last_7d">Last 7 days</SelectItem>
            <SelectItem value="last_30d">Last 30 days</SelectItem>
            <SelectItem value="last_60d">Last 60 days</SelectItem>
            <SelectItem value="last_90d">Last 90 days</SelectItem>
            <SelectItem value="last_quarter">Last Quarter</SelectItem>
            <SelectItem value="last_year">Last Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Workflows */}
      <div className="space-y-2">
        <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Workflows (leave empty for all)</Label>
        <p className="text-xs tracking-[0.12px] text-[#727272]">
          Filter to specific workflows OR leave empty to see insights from ALL workflows
        </p>
        <div className="flex flex-wrap gap-2">
          {WORKFLOW_MANIFESTS.map((workflow) => (
            <button
              key={workflow.workflowId}
              onClick={() => toggleItem(workflow.workflowName, selectedWorkflows, setSelectedWorkflows)}
              className={`rounded-3xl px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] transition-all ${
                selectedWorkflows.includes(workflow.workflowName)
                  ? workflow.isCustomerBuilt
                    ? 'bg-[#e9f9ff] text-[#0065b3]'
                    : 'bg-[#1d1d1d] text-[#fafafa]'
                  : 'border border-[#e6e6e6] bg-white text-[#727272] hover:border-[#ff5c04]'
              }`}
            >
              {workflow.workflowName}
              {workflow.isCustomerBuilt && <span className="ml-1">(AI Studio)</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Products */}
      <div className="space-y-2">
        <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Products (leave empty for all)</Label>
        <div className="flex flex-wrap gap-2">
          {MOCK_PRODUCTS.map((product) => (
            <button
              key={product}
              onClick={() => toggleItem(product, selectedProducts, setSelectedProducts)}
              className={`rounded-3xl px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] transition-all ${
                selectedProducts.includes(product)
                  ? 'bg-[#1d1d1d] text-[#fafafa]'
                  : 'border border-[#e6e6e6] bg-white text-[#727272] hover:border-[#ff5c04]'
              }`}
            >
              {product}
            </button>
          ))}
        </div>
      </div>

      {/* Nodes */}
      <div className="space-y-2">
        <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Technology Nodes (leave empty for all)</Label>
        <div className="flex flex-wrap gap-2">
          {MOCK_NODES.map((node) => (
            <button
              key={node}
              onClick={() => toggleItem(node, selectedNodes, setSelectedNodes)}
              className={`rounded-3xl px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] transition-all ${
                selectedNodes.includes(node)
                  ? 'bg-[#1d1d1d] text-[#fafafa]'
                  : 'border border-[#e6e6e6] bg-white text-[#727272] hover:border-[#ff5c04]'
              }`}
            >
              {node}
            </button>
          ))}
        </div>
      </div>

      {/* Business Units */}
      <div className="space-y-2">
        <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Business Units (leave empty for all)</Label>
        <div className="flex flex-wrap gap-2">
          {MOCK_BUSINESS_UNITS.map((bu) => (
            <button
              key={bu}
              onClick={() => toggleItem(bu, selectedBUs, setSelectedBUs)}
              className={`rounded-3xl px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] transition-all ${
                selectedBUs.includes(bu)
                  ? 'bg-[#1d1d1d] text-[#fafafa]'
                  : 'border border-[#e6e6e6] bg-white text-[#727272] hover:border-[#ff5c04]'
              }`}
            >
              {bu}
            </button>
          ))}
        </div>
      </div>

      {/* Foundries */}
      <div className="space-y-2">
        <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Foundries (leave empty for all)</Label>
        <div className="flex flex-wrap gap-2">
          {MOCK_FOUNDRIES.map((foundry) => (
            <button
              key={foundry}
              onClick={() => toggleItem(foundry, selectedFoundries, setSelectedFoundries)}
              className={`rounded-3xl px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] transition-all ${
                selectedFoundries.includes(foundry)
                  ? 'bg-[#1d1d1d] text-[#fafafa]'
                  : 'border border-[#e6e6e6] bg-white text-[#727272] hover:border-[#ff5c04]'
              }`}
            >
              {foundry}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between border-t border-[#e6e6e6] pt-4">
        <Button
          variant="outline"
          onClick={() => {
            setSelectedProducts([])
            setSelectedNodes([])
            setSelectedBUs([])
            setSelectedFoundries([])
            setSelectedWorkflows([])
            setDateRange('last_quarter')
          }}
          className="h-10 rounded-lg border-[#e6e6e6] bg-white px-4 text-sm font-bold tracking-[0.14px] text-[#272727] hover:bg-[#fafafa]"
        >
          Reset to Portfolio
        </Button>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            className="h-10 rounded-lg border-0 bg-[#f5f5f5] px-4 text-sm font-bold tracking-[0.14px] text-[#272727] hover:bg-[#e6e6e6]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="h-10 rounded-lg bg-[#1d1d1d] px-4 text-sm font-bold tracking-[0.14px] text-[#fafafa] hover:bg-[#272727]"
          >
            Apply Scope
          </Button>
        </div>
      </div>
    </div>
  )
}
